import { Component, OnInit, Input, ViewChild, SimpleChanges, forwardRef } from '@angular/core';
import { 
    IonPopover, IonLabel, IonIcon, IonSelect, IonSelectOption, IonButton
} from '@ionic/angular/standalone';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ReactiveFormsModule, ControlContainer, FormGroupDirective, Validators } from '@angular/forms';
import moment from 'moment';
import { Subscription } from 'rxjs';

import { MarvDateToStringPipe } from '@marv/pipes';
import { MarvSpeechToTextComponent } from '@marv/components'; 
import { MarvDateService } from '@marv/services';

class Day {
    date: Date;
    differentMonth: boolean;
    today: boolean;
}

class Week {
    startDate: Date;
    endDate: Date;   
    currentWeek: boolean;
    selectedWeek: boolean;
    days: Array<Day>; 
}

class Month {
    name: string;
    num: number;
}

@Component({
    selector: 'marv-week-select',
    templateUrl: './select.component.html',
    styleUrls: ['./select.component.scss'],
    imports: [
        IonPopover, IonLabel, IonIcon, IonSelect, IonSelectOption, IonButton, TranslatePipe, ReactiveFormsModule, 
        MarvDateToStringPipe, forwardRef(() => MarvSpeechToTextComponent)
    ],
    viewProviders: [
        {
            provide: ControlContainer,
            useExisting: FormGroupDirective
        }
    ]
})
export class MarvWeekSelectComponent  implements OnInit {

    @Input({alias : 'label', required : false}) labelInput: string = '';
    @Input({alias : 'fixedLabel', required : false}) fixedLabel: boolean = false;
    @Input({alias : 'icon', required : false}) iconInput: string = 'calendar-outline';
    @Input({alias : 'formName', required : true}) formName: string|number = '';
    @Input({alias : 'errorText', required : false}) errorText: string = this.translateService.instant('MSG_ERROR_SELECT_MANDATORY');
    @Input({alias : 'disabled', required : false}) disabled: boolean = false;
    @Input({alias : 'noMargin', required : false}) noMargin: boolean = false;
    @Input({alias : 'withSpeechToText', required : false}) withSpeechToText: boolean = false;

    @ViewChild('popoverWeekDate') popoverWeekDate: IonPopover;
    @ViewChild('marvSpeechToText') marvSpeechToText: MarvSpeechToTextComponent;

    weeks: Array<Week> = [];
    week: Date;
    months: Array<Month> = [];
    month: number;
    years: Array<number> = [];
    year: number;
    
    isRequired: boolean = false;
    
    updateStatusChange: Subscription;
    
    constructor(
        public translateService: TranslateService,
        public formGroupDirective: FormGroupDirective
    ) {
        
        this.months = [
            {num: 1, name: 'MONTH_JANUARY'},
            {num: 2, name: 'MONTH_FEBRUARY'},
            {num: 3, name: 'MONTH_MARCH'},
            {num: 4, name: 'MONTH_APRIL'},
            {num: 5, name: 'MONTH_MAY'},
            {num: 6, name: 'MONTH_JUNE'},
            {num: 7, name: 'MONTH_JULY'},
            {num: 8, name: 'MONTH_AUGUST'},
            {num: 9, name: 'MONTH_SEPTEMBER'},
            {num: 10, name: 'MONTH_OCTOBER'},
            {num: 11, name: 'MONTH_NOVEMBER'},
            {num: 12, name: 'MONTH_DECEMBER'},
        ];
        
        let year = parseInt(moment().format('YYYY'));
        for(let i=year-3;i<=year+1;i++){
            this.years.push(i);
        }
        
    }

    ngOnInit() {        
        let weekDate = this.formGroupDirective.form.controls[this.formName].value;
        if(weekDate){
            let m = moment(weekDate);
            if(m.isValid()){
                this.week = weekDate;
                this.month = parseInt(m.format('MM'));
                this.year = parseInt(m.format('YYYY'));
                this.getWeeks();
            }
        }
        this.isRequired = this.formGroupDirective.form.controls[this.formName].hasValidator(Validators.required);
        this.updateStatusChange = this.formGroupDirective.form.controls[this.formName].statusChanges.subscribe((s) => {
            this.isRequired = this.formGroupDirective.form.controls[this.formName].hasValidator(Validators.required);
        }); 
    }
    
    async ngOnChanges(changes: SimpleChanges) {                
        if(changes['disabled'] && changes['disabled'].currentValue !== null){    
            if(this.disabled === true){
                this.formGroupDirective.form.controls[this.formName].disable({emitEvent: false});
            }
            else{
                this.formGroupDirective.form.controls[this.formName].enable({emitEvent: false});
            }
        }
    }
    
    ngOnDestroy() {  
        if(this.updateStatusChange){
            this.updateStatusChange.unsubscribe();
        }
    }
    
    getWeeks(){
        this.weeks = [];
        let weeks: Array<Week> = [];
        let now = moment();
        let dateMonth = moment(this.year+'-'+this.month.toString().padStart(2, '0')+'-01').toDate();
        let dateStartCurrentWeek = moment(MarvDateService.GetFirstDayOfWeek(now.toDate()));
        let startDate = moment(MarvDateService.GetFirstDayOfWeek(MarvDateService.GetFirstDayOfMonth(dateMonth)));
        let endDate = moment(MarvDateService.GetLastDayOfWeek(MarvDateService.GetLastDayOfMonth(dateMonth)));
        let cpt=0;
        while(startDate <= endDate && cpt < 50){
            let m = moment(startDate);
            let day: Day = {
                date: startDate.toDate(),
                differentMonth: (parseInt(m.format('MM')) !== this.month),
                today: (m.format('YYYY-MM-DD') === now.format('YYYY-MM-DD'))
            };
            let dateStartWeek = MarvDateService.GetFirstDayOfWeek(startDate.toDate());
            let dateEndWeek = MarvDateService.GetLastDayOfWeek(startDate.toDate());
            let existWeek = weeks.find(w => moment(w.startDate).format('YYYY-MM-DD') === moment(dateStartWeek).format('YYYY-MM-DD'));
            
            if(existWeek){
                existWeek.days.push(day);
            }
            else{
                weeks.push({                    
                    startDate: dateStartWeek,
                    endDate: dateEndWeek,
                    currentWeek: (moment(dateStartWeek).format('YYYY-MM-DD') === dateStartCurrentWeek.format('YYYY-MM-DD')),
                    selectedWeek: (moment(dateStartWeek).format('YYYY-MM-DD') === moment(this.week).format('YYYY-MM-DD')),
                    days: [day]
                });
            }
            startDate.add(1, 'day');
            cpt++;
        }
        this.weeks = weeks;
    }
    
    selectYear($event: any){
        if($event.detail?.value){
            this.year = $event.detail?.value;
            this.getWeeks();
        }
    }
    
    selectMonth($event: any){
        if($event.detail?.value){
            this.month = $event.detail?.value;
            this.getWeeks();
        }
    }
    
    selectWeek(week: Week){
        this.week = week.startDate;
        for(let w of this.weeks){
            w.selectedWeek = (moment(w.startDate).format('YYYY-MM-DD') === moment(this.week).format('YYYY-MM-DD'));
        }
    }
    
    confirmWeek(){
        if(this.week){
            this.formGroupDirective.form.controls[this.formName].setValue(this.week);
        }
        this.closeWeekPicker();
    }
    
    closeWeekPicker(){
        this.popoverWeekDate.dismiss();
    }
    
    speechToText($event: any){
        $event.stopPropagation();  
        if(!this.disabled){
            this.marvSpeechToText.open();
        }
    }
    
    onDismissSpeechToText(text: string){
        if(text !== '' && this.formGroupDirective.form.controls[this.formName]){
            let date = MarvDateService.ParseFromString(text);
            if(date){
                this.formGroupDirective.form.controls[this.formName].setValue(MarvDateService.GetFirstDayOfWeek(date));
            }
        }
    }
}
